import { isNumber } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { SliderInput } from '@meitu/saas-ui';
import { hexToHsva } from '@meitu/saas-colors/dist/esm/utils/color-convert';
import { TextLayerExtension } from '@meitu/saas-editor-core/es/editor/layer-extensions/text-layer';
import { type ILayerProps, type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { EffectPropsContainer } from '@/components/effect-props-container';
import LabelAction from '@/components/label-action';
import { useEditor } from '@/hooks/use-editor';
import { useLocaleStore } from '@/i18n';
import { TextPanelContext } from '../../context';
import { useEditingRange, useLayerProps, useTextContent } from '../../hooks';
import { Tracker } from '../../utils';
import { TextColorPicker } from '../text-color-picker';

export const FillInspector: React.FC = () => {
    const { layerId, recordId } = useContext(TextPanelContext)!;

    const { $t } = useLocaleStore();

    const editor = useEditor();
    const props = useLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        const { color, texture, textGradient } = layerData;

        return {
            color,
            texture,
            textGradient,
        };
    });

    const color = props ? props.color : '#000000ff';
    const texture = props ? props.texture : null;
    const textGradient = props ? props.textGradient : null;

    const editingRange = useEditingRange(editor);
    const textContent = useTextContent(editor, editingRange);

    const [fillOpacity, setFillOpacity] = useState(() => {
        const hsva = hexToHsva(color);
        return Math.round(hsva.a * 100);
    });

    const disableFillOpacity =
        (editingRange && !editingRange.isCollapsed) ||
        textContent != null ||
        texture != null ||
        (textGradient != null && textGradient.colors.length > 1);

    const originLayer = useRef<ILayerProps | null>(null);

    const handleChange = (value: number) => {
        if (!isNumber(value)) return;

        if (originLayer.current == null) {
            originLayer.current = editor.stage.getLayer(layerId, true);
        }

        const finalValue = Math.round(value);

        setFillOpacity(finalValue);
        editor.getInstance(TextLayerExtension).setFillOpacity(layerId, finalValue);
    };

    const handleAfterChange = (value: number) => {
        if (!isNumber(value)) return;

        const finalValue = Math.round(value);

        setFillOpacity(finalValue);

        editor.getInstance(TextLayerExtension).setFillOpacity(layerId, finalValue, {
            originLayer: originLayer.current ? originLayer.current : undefined,
        });

        originLayer.current = null;

        Tracker.setFillOpacity({
            recordId,
            stageType: editor.stage.getType(),
            opacity: finalValue,
            color,
            panelPosition: 'secondary',
        });
    };

    useEffect(() => {
        if (disableFillOpacity) return;

        const hsva = hexToHsva(color);
        setFillOpacity(Math.round(hsva.a * 100));
    }, [color, disableFillOpacity]);

    return (
        <EffectPropsContainer icon="PaintBucketBoldFill" title={$t((v) => v.textStyleAdd.fillOrGradient)}>
            <div className="text-fill-inspector">
                {/* 填充色 */}
                <LabelAction title={$t((v) => v.textStyleAdd.color)} align>
                    <TextColorPicker useForFill size={{ width: 58, height: 26 }} panelPosition="secondary" />
                </LabelAction>

                {/* 透明度 */}
                {!disableFillOpacity ? (
                    <LabelAction title={$t((v) => v.textStyleAdd.opacity)} align>
                        <SliderInput
                            showStepBtn
                            value={fillOpacity}
                            onChange={(value) => handleChange(value)}
                            onAfterChange={(value) => handleAfterChange(value)}
                            min={0}
                            max={100}
                            step={1}
                            precision={1}
                            type="horizontal"
                        />
                    </LabelAction>
                ) : null}
            </div>
        </EffectPropsContainer>
    );
};
